import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { stipulationKeys } from "../../../../stipulations/api/stipulationKeys";
import { dscWorkbenchKeys } from "../../../api/dscWorkbenchKeys";
import { saveResidenceCallScriptMock } from "./callScriptResidence.mock";
import {
  buildResidenceCallScriptSaveBody,
  type ResidenceCallScriptSaveContext,
  type ResidenceCallScriptSaveResponse,
} from "./callScriptResidence.savePayload";
import { callScriptResidenceKeys } from "./callScriptResidenceKeys";

/** Input for saving a completed residence Call Script. */
export interface SaveCallScriptResidenceInput extends ResidenceCallScriptSaveContext {
  approvalId: string;
  applicantId: string;
}

/**
 * Mutation hook for saving a completed residence ("Customer Verification")
 * Call Script — the modal's Save button. The employment equivalent is
 * `useSaveCallScript`, and this mirrors its contract:
 *
 *   - the request body is built by `buildResidenceCallScriptSaveBody`;
 *   - the mutation no-ops (resolving `undefined`) when a force-exit was
 *     detected between the caller's own check and the mutation running, so
 *     the modal knows not to wipe what the analyst just captured;
 *   - on success the history query, the dashboard, and the stipulations list
 *     are invalidated rather than patched locally — the save response is
 *     deliberately thin (persisted identifiers only), and a saved call can
 *     change residence data and trigger a stipulation recalc.
 *
 * TODO(BFF): served by `saveResidenceCallScriptMock` until the endpoint
 * exists. The generated client (1.0.780) has no residence verification API;
 * once it does, the mutationFn body becomes
 * `(await api.saveResidenceCallScript(approvalId, applicantId, buildResidenceCallScriptSaveBody(context))).data`
 * and callScriptResidence.mock.ts can be deleted.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSaveCallScriptResidence() {
  const queryClient = useQueryClient();
  const shouldForceExit = useForceExitStore((state) => state.shouldForceExit);

  return useMutation({
    mutationFn: async ({
      approvalId,
      applicantId,
      ...context
    }: SaveCallScriptResidenceInput): Promise<ResidenceCallScriptSaveResponse | undefined> => {
      if (shouldForceExit) {
        return undefined;
      }
      return saveResidenceCallScriptMock(approvalId, applicantId, buildResidenceCallScriptSaveBody(context));
    },
    onSuccess: (result, { approvalId, applicantId }) => {
      if (!result) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: callScriptResidenceKeys.history({ approvalId, applicantId }),
      });
      void queryClient.invalidateQueries({ queryKey: dscWorkbenchKeys.detail({ approvalId }) });
      void queryClient.invalidateQueries({ queryKey: stipulationKeys.lists() });
    },
  });
}
